import { Outlet, useMatches } from 'react-router';
import { css } from '@understory-io/leaf/css';

import { getHeaderConfig } from '~/lib/header-config';
import { useRootData } from '~/root';
import { Header } from '~/ui/app-shell/header';

import type { Route } from './+types/giftcard.layout';

/**
 * Layout for the gift-card flow. Like the checkout, it lives outside
 * `app.layout`, so it renders its own reduced chrome: the storefront header
 * with only the language switcher (no nav menu / gift-card icon), no site
 * footer, on the plain `surface.base` canvas from <Page>.
 */
export default function GiftCardLayout({ params }: Route.ComponentProps) {
  const storefront = useRootData();
  const matches = useMatches();

  const headerConfig = getHeaderConfig(matches, params.locale, {
    hasMultipleListedExperiences: storefront.listedExperiencesCount > 1,
  });

  return (
    <div
      className={css({
        flexGrow: '1',
        display: 'flex',
        flexDirection: 'column',
        // Breathing room so the last fields clear the browser chrome and the
        // form scrolls fully into view (mirrors the checkout layout).
        paddingBottom: '[200px]',
      })}
    >
      <Header
        companyName={storefront.companyName}
        companyWebsite={storefront.companyWebsite}
        logo={storefront.companyLogo}
        config={headerConfig}
        // No `features` → the nav menu and gift-card icon are omitted, leaving
        // just the language switcher, matching the checkout chrome.
        availableLanguages={storefront.languages}
        resolvedLocale={storefront.locale}
        defaultLanguage={storefront.defaultLanguage}
      />
      <Outlet />
    </div>
  );
}
